import {
  addDays,
  endOfMonth,
  endOfWeek,
  format,
  isSameMonth,
  isToday,
  isWeekend,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ChevronDown } from "react-bootstrap-icons";
import { cn } from "@utils";
import { itemAnchorDate } from "../common";
import EventChip from "./EventChip";

const FULL_WEEK = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri"];

const MonthGrid = ({ viewDate, items, onSelectItem, onCreateAt, weekdaysOnly = false }) => {
  const days = useMemo(() => {
    const gridStart = startOfWeek(startOfMonth(viewDate), { weekStartsOn: 1 });
    const gridEnd = endOfWeek(endOfMonth(viewDate), { weekStartsOn: 1 });
    const result = [];
    for (let day = gridStart; day <= gridEnd; day = addDays(day, 1)) {
      result.push(day);
    }
    return result;
  }, [viewDate]);

  const itemsByDay = useMemo(() => {
    const map = new Map();
    for (const item of items) {
      const anchor = itemAnchorDate(item);
      if (!anchor) continue;
      const key = format(anchor, "yyyy-MM-dd");
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(item);
    }
    for (const list of map.values()) {
      list.sort((a, b) => {
        if (a.allDay !== b.allDay) return a.allDay ? -1 : 1;
        return itemAnchorDate(a) - itemAnchorDate(b);
      });
    }
    return map;
  }, [items]);

  // Weekdays-only drops Sat/Sun for wider slots. Weekend items aren't shown in that mode.
  const gridDays = weekdaysOnly ? days.filter((day) => !isWeekend(day)) : days;
  const cols = weekdaysOnly ? 5 : 7;
  const weeks = Math.max(1, Math.ceil(gridDays.length / cols));
  const labels = weekdaysOnly ? WEEKDAYS : FULL_WEEK;
  const columnsStyle = { gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` };

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-lg border border-zinc-200 bg-white shadow-sm">
      <div className="grid shrink-0 border-b border-zinc-200 bg-zinc-50" style={columnsStyle}>
        {labels.map((day) => (
          <div
            key={day}
            className="px-2 py-1.5 text-center text-xs font-medium text-zinc-500"
          >
            {day}
          </div>
        ))}
      </div>
      {/* Fixed-height grid (rows share the available height) so each day cell is bounded and its
          own item list scrolls independently rather than truncating at "+N more". */}
      <div
        className="grid min-h-0 flex-1"
        style={{ ...columnsStyle, gridTemplateRows: `repeat(${weeks}, minmax(0, 1fr))` }}
      >
        {gridDays.map((day) => (
          <DayCell
            key={format(day, "yyyy-MM-dd")}
            day={day}
            inMonth={isSameMonth(day, viewDate)}
            today={isToday(day)}
            dayItems={itemsByDay.get(format(day, "yyyy-MM-dd")) ?? []}
            onSelectItem={onSelectItem}
            onCreateAt={onCreateAt}
          />
        ))}
      </div>
    </div>
  );
};

// A day cell whose item list scrolls independently. Because in-cell scroll is easy to miss, a bottom
// fade + down-chevron appears only when there's more below, and hides once scrolled to the end — the
// cue that there's more, without the clutter of a permanent "+N more".
const DayCell = ({ day, inMonth, today, dayItems, onSelectItem, onCreateAt }) => {
  const scrollRef = useRef(null);
  const [moreBelow, setMoreBelow] = useState(false);

  const checkOverflow = () => {
    const el = scrollRef.current;
    if (!el) return;
    setMoreBelow(el.scrollHeight - el.scrollTop - el.clientHeight > 1);
  };

  useLayoutEffect(checkOverflow, [dayItems]);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const scrollDown = (event) => {
    event.stopPropagation();
    scrollRef.current?.scrollBy({ top: scrollRef.current.clientHeight * 0.8, behavior: "smooth" });
  };

  return (
    <div
      onClick={() => onCreateAt?.(day)}
      className={cn(
        "flex min-h-0 cursor-pointer flex-col border-b border-r border-zinc-100 p-1.5 transition-colors hover:bg-zinc-50",
        !inMonth && "bg-zinc-50/60",
      )}
    >
      <div className="flex shrink-0 justify-end">
        <span
          className={cn(
            "flex size-6 items-center justify-center rounded-full text-xs",
            today
              ? "bg-primary-500 font-semibold text-white"
              : inMonth
                ? "text-zinc-700"
                : "text-zinc-400",
          )}
        >
          {format(day, "d")}
        </span>
      </div>

      <div className="relative mt-0.5 min-h-0 flex-1">
        <div
          ref={scrollRef}
          onScroll={checkOverflow}
          className="h-full space-y-1 overflow-y-auto"
        >
          {dayItems.map((item) => (
            <EventChip
              key={item.occurrenceKey ?? item.uid}
              item={item}
              onClick={onSelectItem}
            />
          ))}
        </div>

        {moreBelow && (
          <button
            type="button"
            onClick={scrollDown}
            aria-label="Scroll for more"
            className="absolute inset-x-0 bottom-0 flex h-5 items-end justify-center bg-gradient-to-t from-white via-white/80 to-transparent"
          >
            <ChevronDown className="size-3.5 text-zinc-500" />
          </button>
        )}
      </div>
    </div>
  );
};

export default MonthGrid;
